import { nsiService, storeService } from "@services";
import { useCallback, useEffect, useRef, useState } from "react";

export enum IMPORT_DATA_TYPE {
    DISCOUNTS = "discounts",
    STORES = "stores",
    PRICES = "prices",
    QUANTITY = "quantity",
    NEW_ENTITIES = "new_entities",
    EXIST_ENTITIES = "exist_entities",
}

// фазы для нового импорта сущностей (async, load_entities_csv):
//  parsing  — реальный прогресс (строка X из N), ядро пишет в csv_load_status
//  applying — пересчёт матвью в ядре (~75с, условный таймер — PG не даёт прогресс REFRESH)
//  syncing  — перенос в cms/на витрину (~45с, условный таймер)
export type ImportPhase = "parsing" | "applying" | "syncing" | "done" | "error";

export interface ImportStatus {
    phase: ImportPhase;
    processed?: number;
    total?: number;
    /** 0..100 для условных фаз applying/syncing */
    progress?: number;
    message?: string;
    /**
     * Имя загружаемого файла. Хранится здесь, а не читается из
     * DropzoneField.files — тот чистит свой список сразу после того, как
     * резолвится onSubmit, а для этого (async, polling) флоу onSubmit
     * резолвится сразу после отправки на сервер, а не после реального
     * завершения импорта — поэтому к моменту показа прогресс-бара дропзона
     * уже пустая (найдено пользователем 01.10.2026).
     */
    fileName?: string;
}

const APPLYING_MS = 75_000;
const SYNCING_MS = 45_000;
const POLL_MS = 2_000;

const useImportDataControls = (
    importType: IMPORT_DATA_TYPE,
    { entityTypeId = 0, submitCallback = () => {} }
) => {
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState<string>("");
    const [status, setStatus] = useState<ImportStatus | null>(null);

    const pollRef = useRef<ReturnType<typeof setInterval>>();
    const tickRef = useRef<ReturnType<typeof setInterval>>();
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const fileNameRef = useRef<string>("");

    const defaultSuccessMessage = "Данные загружены";
    const defaultErrorMessage = "Произошла ошибка при загрузке данных из файла";

    const clearAll = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (tickRef.current) clearInterval(tickRef.current);
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    useEffect(() => clearAll, [clearAll]);

    const setStatusWithFile = useCallback(
        (partial: Omit<ImportStatus, "fileName">) => {
            setStatus({ ...partial, fileName: fileNameRef.current || undefined });
        },
        []
    );

    // условная фаза с таймером: рисуем бар 0..97% за durationMs, затем onDone
    const runTimedPhase = useCallback(
        (phase: "applying" | "syncing", durationMs: number, onDone: () => void) => {
            const startedAt = Date.now();
            setStatusWithFile({ phase, progress: 0 });
            tickRef.current = setInterval(() => {
                const p = Math.min(97, ((Date.now() - startedAt) / durationMs) * 100);
                setStatusWithFile({ phase, progress: p });
            }, 1000);
            timerRef.current = setTimeout(() => {
                if (tickRef.current) clearInterval(tickRef.current);
                onDone();
            }, durationMs);
        },
        [setStatusWithFile]
    );

    const startPolling = useCallback(
        (csvLoadStatusId: number) => {
            setStatusWithFile({ phase: "parsing", processed: 0, total: 0 });
            pollRef.current = setInterval(async () => {
                let s;
                try {
                    s = await nsiService.getCsvLoadStatus(csvLoadStatusId);
                } catch {
                    return; // временная ошибка сети — ждём следующий тик
                }
                if (!s) return;

                if (s.phase === "error") {
                    clearAll();
                    setStatusWithFile({ phase: "error", message: s.message });
                    setError(s.message || defaultErrorMessage);
                    return;
                }

                if (s.status) {
                    // парсинг + запись в базовые таблицы завершены
                    clearAll();
                    const doneMessage = s.message || defaultSuccessMessage;
                    runTimedPhase("applying", APPLYING_MS, () => {
                        runTimedPhase("syncing", SYNCING_MS, () => {
                            setStatusWithFile({ phase: "done", message: doneMessage });
                            setSuccess(doneMessage);
                            submitCallback();
                        });
                    });
                    return;
                }

                setStatusWithFile({
                    phase: "parsing",
                    processed: s.processed_rows ?? 0,
                    total: s.total_rows ?? 0,
                });
            }, POLL_MS);
        },
        [clearAll, runTimedPhase, setStatusWithFile, submitCallback]
    );

    const handleChangeFile = () => {
        clearAll();
        setSuccess("");
        setError("");
        setStatus(null);
        fileNameRef.current = "";
    };

    const handleSubmit = async (files: File[]) => {
        clearAll();
        setSuccess("");
        setError("");
        setStatus(null);

        switch (importType) {
            case IMPORT_DATA_TYPE.EXIST_ENTITIES:
                return uploadFileToEntitiesService(files, "exist");
            case IMPORT_DATA_TYPE.NEW_ENTITIES:
                return uploadFileToEntitiesService(files, "new");
            case IMPORT_DATA_TYPE.PRICES:
                return uploadFileToStorePriceService(files);
            case IMPORT_DATA_TYPE.QUANTITY:
                return uploadFileToStoreQuantityService(files);
            case IMPORT_DATA_TYPE.STORES:
                return uploadFileToStoreService(files);
            case IMPORT_DATA_TYPE.DISCOUNTS:
                return uploadFileToStoreDiscountsService(files);
        }
    };

    const uploadFileToEntitiesService = async (files: File[], type: "new" | "exist") => {
        if (!files || !importType || !entityTypeId) return;

        try {
            if (type === "new") {
                // имя файла — для статус-баннера (см. ImportStatus.fileName).
                // Сохраняем ДО запроса: DropzoneField чистит свой список файлов
                // сразу после того, как резолвится onSubmit, а тот резолвится
                // сразу после отправки на сервер — к моменту показа прогресса
                // своего списка у дропзоны уже нет.
                fileNameRef.current = files[0]?.name ?? "";
                // async: ядро создаёт задачу, прогресс — поллингом csv_load_status
                const { message, csv_load_status_id } = await nsiService.uploadCSVdata(
                    files[0],
                    entityTypeId
                );
                if (csv_load_status_id) {
                    startPolling(csv_load_status_id);
                } else {
                    // старое ядро без id — фолбэк на прежнее поведение
                    setSuccess(message);
                    submitCallback();
                }
                return;
            }

            // exist: /nsi/entity_view_snapshop обрабатывается синхронно — просто ждём ответ
            const msg = await nsiService.uploadEntityShapshot(files[0], entityTypeId);
            setSuccess(msg);
            submitCallback();
        } catch (err) {
            const message = err.response?.data.result || defaultErrorMessage;
            setError(typeof message === "string" ? message : defaultErrorMessage);
        }
    };

    const uploadFileToStorePriceService = async (files: File[]) => {
        if (!files) return;

        try {
            const { result } = await storeService.uploadPriceCSVdata(files[0]);
            setSuccess(result || defaultSuccessMessage);
            submitCallback();
        } catch (err) {
            const message = err.response?.data.result || defaultErrorMessage;
            setError(message);
        }
    };

    const uploadFileToStoreDiscountsService = async (files: File[]) => {
        if (!files) return;
        try {
            const { result } = await storeService.uploadDiscountCSVdata(files[0]);
            setSuccess(result || defaultSuccessMessage);
            submitCallback();
        } catch (err) {
            const message = err.response?.data.result || defaultErrorMessage;
            setError(message);
        }
    };

    const uploadFileToStoreQuantityService = async (files: File[]) => {
        if (!files) return;

        try {
            const { result } = await storeService.uploadQuantityCSVdata(files[0]);
            setSuccess(result || defaultSuccessMessage);
            submitCallback();
        } catch (err) {
            const message = err.response?.data.result || defaultErrorMessage;
            setError(message);
        }
    };

    const uploadFileToStoreService = async (files: File[]) => {
        if (!files.length) return;

        try {
            const { result } = await storeService.uploadCSVStoreFile(files[0]);
            setSuccess(result ?? defaultSuccessMessage);
            submitCallback();
        } catch (err) {
            const message = err.response?.data.result || defaultErrorMessage;
            setError(message);
        }
    };

    return {
        handleChangeFile,
        handleSubmit,
        success,
        error,
        status,
    };
};

export default useImportDataControls;
